import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: min(500px, 92vw);
  max-height: min(720px, 86vh);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
`;

const Title = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 20px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 12px;
  
  &:before {
    content: '';
    display: block;
    width: 4px;
    height: 20px;
    background-color: #009688;
    border-radius: 2px;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  color: #666;
  padding: 0;

  &:hover {
    color: #333;
  }
`;

const Body = styled.div`
  padding: 0 24px 24px 24px;
  overflow: auto;
`;

const TurnoCard = styled.div`
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  background: #FBFBFB;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const TimeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Separator = styled.div`
  padding-top: 20px;
  color: #666;
`;

const TrashButtonContainer = styled.div`
  margin-left: auto;
  padding-top: 10px;
`;

const TrashButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #888;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    border-color: #f44336;
    color: #f44336;
    background: #fff5f5;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 16px;
  align-items: flex-start;
`;

const FieldLabel = styled.label`
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  display: block;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  font-size: 14px;
  color: #555;
  outline: none;
  background: #fcfcfc;
  box-sizing: border-box;

  &:focus {
    border-color: #009688;
    background: #fff;
  }
`;

const Counter = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fcfcfc;
  overflow: hidden;
`;

const CounterButton = styled.button`
  width: 40px;
  height: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #888;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f0f0;
    color: #555;
  }
`;

const CounterInput = styled.input`
  flex: 1;
  text-align: center;
  font-size: 15px;
  color: #333;
  font-weight: 500;
  border: none;
  background: transparent;
  width: 100%;
  outline: none;
  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const Footer = styled.div`
  padding: 16px 24px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;

const FooterButton = styled.button`
  flex: 1;
  height: 42px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const ErrorMessage = styled.div`
  margin: 0 24px 8px 24px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #ffebee;
  color: #c62828;
  font-size: 13px;
  font-family: 'Roboto', sans-serif;
`;

function normalizeTurno(turno) {
  return {
    _id: turno?._id,
    de: turno?.de ?? turno?.horarioInicial ?? turno?.inicio ?? '',
    ate: turno?.ate ?? turno?.horarioFinal ?? turno?.fim ?? '',
    vagas: Number.isFinite(turno?.vagas) ? turno.vagas : Number(turno?.vagas ?? 0) || 0,
  };
}

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const timeToMinutes = (timeValue) => {
  if (!TIME_PATTERN.test(timeValue || '')) return null;
  const [hours, minutes] = timeValue.split(':').map(Number);
  return (hours * 60) + minutes;
};

const ModalTurnos = ({ isOpen, onClose, turnos = [], onSave }) => {
  const initialDraft = useMemo(() => (Array.isArray(turnos) ? turnos : []).map(normalizeTurno), [turnos]);
  const [draft, setDraft] = useState(initialDraft);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (!initialDraft || initialDraft.length === 0) {
        setDraft([{ de: '', ate: '', vagas: 1 }]);
      } else {
        setDraft(initialDraft);
      }
      setErrorMessage('');
    }
  }, [isOpen, initialDraft]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const updateTurno = (index, patch) => {
    setDraft((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
    setErrorMessage('');
  };

  const handleAddTurno = () => {
    setDraft((prev) => [...prev, { de: '', ate: '', vagas: 1 }]);
  };

  const handleRemoveTurno = (index) => {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const getDuration = (de, ate) => {
      const inicio = timeToMinutes(de);
      const fim = timeToMinutes(ate);
      if (inicio === null || fim === null) return null;
      return fim - inicio;
    };

    // Horário de início obrigatório
    const semHorarioInicio = draft.some((t) => !t.de);
    if (semHorarioInicio) {
      setErrorMessage('O horário de início é obrigatório para todos os turnos.');
      return;
    }

    // Horário de término obrigatório
    const semHorarioFim = draft.some((t) => !t.ate);
    if (semHorarioFim) {
      setErrorMessage('O horário de término é obrigatório para todos os turnos.');
      return;
    }

    const horarioInvalido = draft.findIndex((t) => TIME_PATTERN.test(t.de || '') === false || TIME_PATTERN.test(t.ate || '') === false);
    if (horarioInvalido !== -1) {
      setErrorMessage(`O Turno ${horarioInvalido + 1} possui um horário inválido. Use o formato HH:mm.`);
      return;
    }

    const horarioInvertido = draft.findIndex((t) => {
      const duracao = getDuration(t.de, t.ate);
      return duracao === null || duracao <= 0;
    });
    if (horarioInvertido !== -1) {
      setErrorMessage(`O Turno ${horarioInvertido + 1} possui um horário inválido. O término deve ser maior que o início no mesmo dia.`);
      return;
    }

    // Cada turno deve ter no mínimo 1h
    const turnoCurto = draft.findIndex((t) => getDuration(t.de, t.ate) < 60);
    if (turnoCurto !== -1) {
      setErrorMessage(`O Turno ${turnoCurto + 1} deve ter no mínimo 1 hora de duração.`);
      return;
    }

    // Total dos turnos não pode ultrapassar 24h
    const totalMinutos = draft.reduce((acc, t) => acc + getDuration(t.de, t.ate), 0);
    if (totalMinutos > 1440) {
      setErrorMessage('A soma dos turnos não pode ultrapassar 24 horas.');
      return;
    }

    // Verificar turnos duplicados (mesmo horário de início e fim)
    const chaves = draft.map(t => `${t.de}-${t.ate}`);
    const indiceDuplicado = chaves.findIndex((chave, i) => chaves.indexOf(chave) !== i);
    if (indiceDuplicado !== -1) {
      setErrorMessage(`O Turno ${indiceDuplicado + 1} possui o mesmo horário de outro turno. Cada turno deve ter um horário único.`);
      return;
    }

    const turnosOrdenados = draft
      .map((turno, index) => ({
        index,
        inicio: timeToMinutes(turno.de),
        fim: timeToMinutes(turno.ate),
      }))
      .sort((a, b) => a.inicio - b.inicio);

    setErrorMessage('');
    onSave(draft);
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <Title>Turnos</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <Body>
          {draft.map((turno, index) => (
            <TurnoCard key={index}>
              <TimeRow>
                <TimeBlock>
                  <FieldLabel>Das</FieldLabel>
                  <Input
                    type="time"
                    value={turno.de}
                    onChange={(e) => updateTurno(index, { de: e.target.value })}
                    style={{ width: '110px', textAlign: 'center' }}
                  />
                </TimeBlock>

                <Separator>-</Separator>

                <TimeBlock>
                  <FieldLabel>Até</FieldLabel>
                  <Input
                    type="time"
                    value={turno.ate}
                    onChange={(e) => updateTurno(index, { ate: e.target.value })}
                    style={{ width: '110px', textAlign: 'center' }}
                  />
                </TimeBlock>

                <TrashButtonContainer>
                  <TrashButton
                    type="button"
                    title="Excluir turno"
                    onClick={() => handleRemoveTurno(index)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </TrashButton>
                </TrashButtonContainer>
              </TimeRow>

              <div style={{ maxWidth: '140px' }}>
                <FieldLabel>Vagas</FieldLabel>
                <Counter>
                  <CounterButton
                    type="button"
                    onClick={() => updateTurno(index, { vagas: Math.max(1, (turno.vagas || 1) - 1) })}
                  >
                    −
                  </CounterButton>
                  <CounterInput
                    type="number"
                    min="1"
                    value={turno.vagas || ''}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      updateTurno(index, { vagas: isNaN(val) ? '' : Math.max(1, val) });
                    }}
                    onBlur={(e) => {
                      if (!e.target.value || parseInt(e.target.value, 10) < 1) {
                        updateTurno(index, { vagas: 1 });
                      }
                    }}
                  />
                  <CounterButton
                    type="button"
                    onClick={() => updateTurno(index, { vagas: (turno.vagas || 0) + 1 })}
                  >
                    +
                  </CounterButton>
                </Counter>
              </div>
            </TurnoCard>
          ))}
        </Body>

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        <Footer>
          <FooterButton style={{ backgroundColor: '#008080' }} onClick={handleAddTurno}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="16"></line>
                <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            Adicionar Turno
          </FooterButton>
          <FooterButton style={{ backgroundColor: '#009673' }} onClick={handleSave}>
            Salvar
          </FooterButton>
        </Footer>
      </ModalContainer>
    </Overlay>
  );
};

export default ModalTurnos;
